import { Component, OnInit, signal } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AlertComponent } from '../../../shared/components/alert/alert.component';
import { LoadingComponent } from '../../../shared/components/loading/loading.component';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { PaginationComponent } from '../../../shared/components/pagination/pagination.component';
import { TagsService, ProductTagsSearchParams } from '../../../core/services/tagging/tags.service';
import { ProductTagListModel } from '../../../core/models/tagging/product-tagging.model';
import { Pageable, PaginationState } from '../../../core/models/common/pageable.model';
import { ProductTagTypeListModel } from '../../../core/models/tagging/product-tag-type.model';
import {TooltipDirective} from '../../../shared/directives';

@Component({
  selector: 'app-product-listing',
  imports: [
    CommonModule,
    FormsModule,
    AlertComponent,
    LoadingComponent,
    ButtonComponent,
    NgOptimizedImage,
    PaginationComponent,
    TooltipDirective,
  ],
  templateUrl: './product-listing.component.html',
  styleUrl: './product-listing.component.scss',
})
export class ProductListingComponent implements OnInit {
  readonly searchType = signal<'ProductId' | 'RefId' | 'SkuId' | 'ProductName'>('RefId');
  readonly searchValue = signal('');
  readonly order = signal<'ASC' | 'DESC'>('DESC');
  readonly size = signal(25);
  readonly tagTypeId = signal<number | null>(null);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly data = signal<ProductTagListModel[]>([]);
  readonly tagTypes = signal<ProductTagTypeListModel[]>([]);

  readonly paginationState = signal<PaginationState>({
    currentPage: 0,
    currentPageItems: 0,
    itemsPerPage: 25,
    totalItems: 0,
    loading: false,
  });

  constructor(private readonly tagsService: TagsService) {}

  ngOnInit(): void {
    this.tagsService.listTypes(1, 100).subscribe({
      next: (res) => {
        this.tagTypes.set(res?.content ?? []);
        this.search();
      },
    });
  }

  private buildParams(page: number): ProductTagsSearchParams {
    const params: ProductTagsSearchParams = {
      page,
      pageSize: this.size(),
      order: this.order(),
    };
    const v = (this.searchValue() ?? '').trim();
    if (v) {
      const t = this.searchType();
      if (t === 'ProductId') params.productId = Number(v);
      if (t === 'RefId') params.refId = v;
      if (t === 'SkuId') params.skuId = Number(v);
      if (t === 'ProductName') params.productName = v;
    }
    const tid = this.tagTypeId();
    if (tid != null && tid > 0) params.tagTypeId = tid;
    return params;
  }

  search(): void {
    this.error.set(null);
    const v = (this.searchValue() ?? '').trim();
    const t = this.searchType();
    if ((t === 'ProductId' || t === 'SkuId') && v) {
      const n = Number(v);
      if (!Number.isInteger(n) || n <= 0) {
        this.error.set('Valor numérico inválido.');
        return;
      }
    }

    this.loading.set(true);
    this.paginationState.update((p) => ({ ...p, loading: true }));

    this.tagsService.getProductTags(this.buildParams(0)).subscribe({
      next: (res: Pageable<ProductTagListModel>) => {
        this.applyResponse(res, 0);
        this.loading.set(false);
      },
      error: () => {
        this.data.set([]);
        this.paginationState.set({
          currentPage: 0,
          currentPageItems: 0,
          itemsPerPage: this.size(),
          totalItems: 0,
          loading: false,
        });
        this.error.set('Falha ao realizar a busca.');
        this.loading.set(false);
      },
    });
  }

  readonly onPageChange = (page: number, pageSize: number): void => {
    this.loading.set(true);
    this.error.set(null);
    this.paginationState.update((p) => ({ ...p, loading: true, itemsPerPage: pageSize }));

    this.tagsService.getProductTags(this.buildParams(page)).subscribe({
      next: (res: Pageable<ProductTagListModel>) => {
        this.applyResponse(res, page);
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Falha ao carregar a página.');
        this.loading.set(false);
        this.paginationState.update((p) => ({ ...p, loading: false }));
      },
    });
  };

  private applyResponse(res: Pageable<ProductTagListModel>, page: number): void {
    const content = res?.content ?? [];
    const pageInfo = res?.page;
    const total = pageInfo?.total ?? 0;
    const size = this.size();

    this.data.set(content);
    this.paginationState.set({
      currentPage: page,
      currentPageItems: content.length,
      itemsPerPage: size,
      totalItems: total,
      loading: false,
    });
  }

  onFormSubmit(): void {
    this.search();
  }
}
